<script setup>
import { useIsMobile } from '~~/composables/useIsMobile'

const props = defineProps({
  audio: {
    type: Object,
    required: true,
  },
  audioGtmClass: {
    type: Object,
    default: null,
  },
})

const isMobile = useIsMobile()

/**
 * Playback
 */

const player = ref(null)
const currentTime = ref(0)
const duration = ref(0)
const progress = computed(() =>
  duration.value ? currentTime.value / duration.value : 1
)
const isPlaying = ref(false)

function togglePlay() {
  if (isPlaying.value) {
    player.value.pause()
  } else {
    player.value.play()
  }
}

/**
 * Seeking
 */
const progressTrack = ref(null)

function seek(event) {
  const bounds = progressTrack.value.getBoundingClientRect()
  const seekProgress =
    (event.clientX - bounds.left) / (bounds.right - bounds.left)
  const progressClamped = Math.max(Math.min(seekProgress, 1), 0)
  player.value.currentTime = progressClamped * duration.value
}

/**
 * Listeners and events
 */

function onTimeUpdate(event) {
  currentTime.value = event.target.currentTime
}

function onDurationChange(event) {
  duration.value = event.target.duration
}

function onPlay() {
  isPlaying.value = true
}

function onPause() {
  isPlaying.value = false
}

function initListeners() {
  player.value.addEventListener('timeupdate', onTimeUpdate)
  player.value.addEventListener('durationchange', onDurationChange)
  player.value.addEventListener('play', onPlay)
  player.value.addEventListener('pause', onPause)
}

function destroyListeners() {
  player.value?.removeEventListener('timeupdate', onTimeUpdate)
  player.value?.removeEventListener('durationchange', onDurationChange)
  player.value?.removeEventListener('play', onPlay)
  player.value?.removeEventListener('pause', onPause)
}

onMounted(() => {
  initListeners()
  duration.value = player.value?.duration
})

onBeforeUnmount(() => {
  destroyListeners()
})
</script>

<template>
  <div class="audio-player">
    <audio ref="player">
      <source :src="audio.url" :type="audio.contentType" />
    </audio>

    <button class="audio-player__toggle" :class="audioGtmClass" @click="togglePlay">
      <template v-if="isPlaying">
        <Icon name="pause" />
        <span class="u-visually-hidden">{{ $t('audioPlayer.pause') }}</span>
      </template>
      <template v-else>
        <Icon name="play" />
        <span class="u-visually-hidden">{{ $t('audioPlayer.play') }}</span>
      </template>
    </button>

    <button v-if="!isMobile" class="audio-player__progress" @click="seek">
      <div class="audio-player__progress-track" ref="progressTrack">
        <div
          class="audio-player__progress-inner"
          :style="{ width: `${progress * 100}%` }"
        />
      </div>
    </button>
  </div>
</template>

<style lang="scss">
@import '~/styles/vendors/sass-mq/mq';
@import '~/styles/utils/variables';

.audio-player {
  display: flex;

  width: 180px;
  @include mq($from: 'l') using ($from) {
    margin-bottom: -10px;
    width: auto;
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 2px;
    padding-right: 11px;
  }

  &__progress {
    width: 100%;
    display: flex;
  }

  &__progress-track {
    background: rgba($brand-white, 0.1);
    height: 2px;
    width: 100%;
    margin: auto 0;
  }

  &__progress-inner {
    background: linear-gradient(103.2deg, #d90afb -13.13%, #31afd4 93.9%);
    opacity: 0.9;
    height: 100%;
    width: 0;
  }

  @include mq($until: 'l') using ($until) {
    &__toggle {
      padding: 0;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(#000, 0.2);

      .icon {
        width: 16px;
        height: 17px;
      }
    }
  }
}
</style>
